// src/pages/AdminProductForm.jsx
// 管理者模式 - 完整商品新增/編輯表單

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Package, Save, X, ArrowLeft, Plus, Trash2 } from 'lucide-react';

const AdminProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sellerId = searchParams.get('sellerId');
  
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(isEditMode);
  const [sellers, setSellers] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    original_price: '',
    brand: '',
    category: '',
    description: '',
    stock: '0',
    seller_id: sellerId || ''
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploadType, setUploadType] = useState('url');
  
  // 顏色列表
  const [colors, setColors] = useState([{ name: '', code: '#000000' }]);
  
  // 特色列表
  const [features, setFeatures] = useState(['']);
  
  // 規格列表
  const [specifications, setSpecifications] = useState([{ key: '', value: '' }]);

  useEffect(() => {
    loadSellers();
    loadAllCategories();
    if (isEditMode) {
      loadProduct();
    }
  }, [id]);

  const loadSellers = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_BASE_URL}/admin/sellers`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.error) throw new Error(result.message);

      setSellers(result.data.sellers || []);
    } catch (err) {
      console.error('載入商家列表失敗:', err);
      alert('載入商家列表失敗: ' + err.message);
    }
  };

  const loadAllCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_BASE_URL}/categories`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        if (!result.error && result.data) {
          setAllCategories(result.data.categories || []);
        }
      }
    } catch (err) {
      console.error('載入分類列表失敗:', err);
    }
  };

  const loadProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_BASE_URL}/products/${id}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.error) throw new Error(result.message);

      const product = result.data;
      setFormData({
        name: product.name || '',
        price: product.price || '',
        original_price: product.original_price || '',
        brand: product.brand || '',
        category: product.category || '',
        description: product.description || '',
        stock: product.stock || '0',
        seller_id: product.seller_id || ''
      });
      
      // 載入圖片
      if (product.images && product.images.length > 0) {
        const mainImage = product.images[0];
        setImageUrl(mainImage.url || '');
        setImagePreview(mainImage.url || '');
        setUploadType('url');
      }

      // 載入顏色
      if (product.colors && product.colors.length > 0) {
        setColors(product.colors.map(c => ({ name: c.name, code: c.code || '#000000' })));
      }

      // 載入特色
      if (product.features && product.features.length > 0) {
        setFeatures(product.features);
      }

      // 載入規格
      if (product.specifications && Object.keys(product.specifications).length > 0) {
        const specs = Object.entries(product.specifications).map(([key, value]) => ({ key, value }));
        setSpecifications(specs);
      }

    } catch (err) {
      console.error('載入商品失敗:', err);
      alert('載入商品失敗: ' + err.message);
      navigate(-1);
    } finally {
      setLoadingProduct(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('圖片檔案不能超過 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('請選擇圖片檔案');
        return;
      }
      
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);
    setImagePreview(url);
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // 顏色管理
  const addColor = () => {
    setColors([...colors, { name: '', code: '#000000' }]);
  };

  const removeColor = (index) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  const updateColor = (index, field, value) => {
    const newColors = [...colors];
    newColors[index][field] = value;
    setColors(newColors);
  };

  // 特色管理
  const addFeature = () => {
    setFeatures([...features, '']);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  // 規格管理
  const addSpecification = () => {
    setSpecifications([...specifications, { key: '', value: '' }]);
  };

  const removeSpecification = (index) => {
    setSpecifications(specifications.filter((_, i) => i !== index));
  };

  const updateSpecification = (index, field, value) => {
    const newSpecs = [...specifications];
    newSpecs[index][field] = value;
    setSpecifications(newSpecs);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.seller_id) {
      alert('請填寫所有必填欄位');
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/products/${id}`
        : `${process.env.REACT_APP_API_BASE_URL}/products`;

      const method = isEditMode ? 'PUT' : 'POST';

      // 準備提交數據
      const submitData = { 
        ...formData,
        // 如果 original_price 為空，設為 null
        original_price: formData.original_price || null
      };
      
      // 處理圖片
      const images = [];
      if (uploadType === 'file' && imageFile) {
        const base64 = await convertFileToBase64(imageFile);
        images.push(base64);
      } else if (uploadType === 'url' && imageUrl) {
        images.push(imageUrl);
      }
      if (images.length > 0) {
        submitData.images = images;
      }

      // 處理顏色（過濾空值）
      const validColors = colors.filter(c => c.name.trim());
      if (validColors.length > 0) {
        submitData.colors = validColors;
      }

      // 處理特色（過濾空值）
      const validFeatures = features.filter(f => f.trim());
      if (validFeatures.length > 0) {
        submitData.features = validFeatures;
      }

      // 處理規格（過濾空值並轉換為物件）
      const validSpecs = specifications.filter(s => s.key.trim() && s.value.trim());
      if (validSpecs.length > 0) {
        const specsObject = {};
        validSpecs.forEach(s => {
          specsObject[s.key] = s.value;
        });
        submitData.specifications = specsObject;
      }

      console.log('提交數據:', submitData);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      if (result.error) throw new Error(result.message);

      alert(isEditMode ? '商品更新成功！' : '商品新增成功！');
      
      if (formData.seller_id) {
        navigate(`/admin/sellers/${formData.seller_id}/products`);
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('儲存失敗:', err);
      alert('儲存失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingProduct) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* 頂部導航 */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            返回
          </button>
        </div>

        {/* 表單卡片 */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-8 h-8 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? '編輯商品' : '新增商品'}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 基本資訊 */}
            <div className="border-b pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">基本資訊</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 商家選擇 */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    商家 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="seller_id"
                    value={formData.seller_id}
                    onChange={handleChange}
                    required
                    disabled={!!sellerId}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">請選擇商家</option>
                    {sellers.map(seller => (
                      <option key={seller.id} value={seller.id}>
                        {seller.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 商品名稱 */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    商品名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="請輸入商品名稱"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* 品牌 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    品牌
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder="請輸入品牌名稱"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* 分類 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    分類
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    list="category-suggestions"
                    placeholder="選擇或輸入分類"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <datalist id="category-suggestions">
                    {allCategories.map((cat, index) => (
                      <option key={index} value={cat} />
                    ))}
                  </datalist>
                </div>

                {/* 售價 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    售價 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    placeholder="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                {/* 原價 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    原價（選填）
                  </label>
                  <input
                    type="number"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="未填寫則不顯示"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    填寫原價可顯示折扣標籤
                  </p>
                </div>

                {/* 庫存 */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    庫存數量
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    min="0"
                    placeholder="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* 商品圖片 */}
            <div className="border-b pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">商品圖片</h2>
              
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="url"
                    checked={uploadType === 'url'}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">圖片網址</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="file"
                    checked={uploadType === 'file'}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">本地上傳</span>
                </label>
              </div>

              {uploadType === 'url' && (
                <input
                  type="url"
                  value={imageUrl}
                  onChange={handleImageUrlChange}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              )}

              {uploadType === 'file' && (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    支援 JPG、PNG、GIF 等圖片格式，檔案大小限制 5MB
                  </p>
                </div>
              )}

              {imagePreview && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">預覽：</p>
                  <img
                    src={imagePreview}
                    alt="預覽"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150?text=Invalid+Image';
                    }}
                  />
                </div>
              )}
            </div>

            {/* 商品顏色 */}
            <div className="border-b pb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">商品顏色（選填）</h2>
                <button
                  type="button"
                  onClick={addColor}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100"
                >
                  <Plus className="w-4 h-4" />
                  新增顏色
                </button>
              </div>

              <div className="space-y-3">
                {colors.map((color, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <input
                      type="text"
                      value={color.name}
                      onChange={(e) => updateColor(index, 'name', e.target.value)}
                      placeholder="顏色名稱（例如：黑色）"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <input
                      type="color"
                      value={color.code}
                      onChange={(e) => updateColor(index, 'code', e.target.value)}
                      className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    {colors.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeColor(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 商品特色 */}
            <div className="border-b pb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">商品特色（選填）</h2>
                <button
                  type="button"
                  onClick={addFeature}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100"
                >
                  <Plus className="w-4 h-4" />
                  新增特色
                </button>
              </div>

              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder={`特色 ${index + 1}（例如：高品質材料）`}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    {features.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 商品規格 */}
            <div className="border-b pb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">商品規格（選填）</h2>
                <button
                  type="button"
                  onClick={addSpecification}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100"
                >
                  <Plus className="w-4 h-4" />
                  新增規格
                </button>
              </div>

              <div className="space-y-3">
                {specifications.map((spec, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <input
                      type="text"
                      value={spec.key}
                      onChange={(e) => updateSpecification(index, 'key', e.target.value)}
                      placeholder="規格名稱（例如：尺寸）"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={spec.value}
                      onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                      placeholder="規格值（例如：10 x 15 cm）"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    {specifications.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpecification(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 商品描述 */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">商品描述</h2>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="請輸入商品描述..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              />
            </div>

            {/* 按鈕 */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    儲存中...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isEditMode ? '更新商品' : '新增商品'}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                取消
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProductForm;